import axios from 'axios';

// Use environment variable for backend URL in production, or relative /api for development
const getBaseURL = (): string => {
  // In production, use the full backend URL from environment variable
  if (process.env.REACT_APP_API_URL) {
    const baseURL = `${process.env.REACT_APP_API_URL}/api`;
    console.log('🔵 Using backend URL from REACT_APP_API_URL:', baseURL);
    return baseURL;
  }
  // In development, use relative URL (will be proxied by setupProxy.js)
  console.log('🔵 Using relative /api URL (development mode)');
  return '/api';
};

const baseURL = getBaseURL();
console.log('🔵 API Client baseURL:', baseURL);

const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Enable session-based authentication
});

// Track if CSRF token fetch is in progress to avoid multiple simultaneous requests
let csrfTokenFetching = false;
let csrfTokenPromise: Promise<string | null> | null = null;

// CSRF token from API response (used when cross-origin - cookie not readable by frontend)
let csrfTokenFromApi: string | null = null;

// Get CSRF token from Django cookie (same-origin) or from last API response (cross-origin)
const getCsrfToken = (): string | null => {
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken' || name === 'csrf_token') {
        const v = decodeURIComponent(value);
        if (v) return v;
      }
    }
  }
  return csrfTokenFromApi;
};

// Fetch CSRF token from server; use response body so it works cross-origin (cookie not readable)
const fetchCsrfToken = async (): Promise<string | null> => {
  if (csrfTokenFetching && csrfTokenPromise) {
    return csrfTokenPromise;
  }
  csrfTokenFetching = true;
  csrfTokenPromise = apiClient.get<{ csrfToken?: string }>('/auth/csrf/')
    .then((res) => {
      const token = res?.data?.csrfToken ?? null;
      if (token) csrfTokenFromApi = token;
      return token;
    })
    .catch((error) => {
      console.error('Failed to fetch CSRF token:', error);
      return null;
    })
    .finally(() => {
      csrfTokenFetching = false;
      csrfTokenPromise = null;
    });
  return csrfTokenPromise;
};

// Request interceptor for adding CSRF token
apiClient.interceptors.request.use(
  async (config) => {
    console.log('🌐 Request interceptor - URL:', config.url, 'Method:', config.method);
    console.log('🌐 Request interceptor - Full config:', config);
    // Remove Content-Type header for GET requests (especially for blob responses)
    if (config.method?.toLowerCase() === 'get' && config.responseType === 'blob') {
      delete config.headers['Content-Type'];
    }
    
    // Only add CSRF token for state-changing methods (except login and csrf endpoints)
    const isStateChanging = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '');
    const isLogin = config.url?.includes('/auth/login/');
    const isCsrfEndpoint = config.url?.includes('/auth/csrf/');
    
    if (isStateChanging && !isLogin && !isCsrfEndpoint) {
      // Always fetch fresh CSRF token before each state-changing request so we never send
      // a stale token (Django may rotate the token after use, especially cross-origin).
      const csrfToken = await fetchCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      } else {
        console.warn('⚠️ No CSRF token available for request:', config.url);
      }
    }
    
    return config;
  },
  (error) => {
    console.error('🌐 Request interceptor - Error:', error);
    return Promise.reject(error);
  }
);

// Track retry attempts to prevent infinite loops
// Use a Map with string keys since WeakMap might not work reliably with request configs
const retryAttempts = new Map<string, number>();

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => {
    console.log('🌐 Response interceptor - Success:', response.config.url, 'Status:', response.status);
    return response;
  },
  async (error) => {
    console.error('🌐 Response interceptor - Error:', error.config?.url, 'Status:', error.response?.status);
    if (error.response?.status === 401) {
      // Handle unauthorized
      console.error('Unauthorized access');
    } else if (error.response?.status === 403) {
      // CSRF error - try to get a fresh token and retry (only once)
      const originalRequest = error.config;
      
      if (!originalRequest) {
        return Promise.reject(error);
      }
      
      // Check if this request has already been retried using a string key
      const requestKey = `${originalRequest.method}_${originalRequest.url}`;
      const retryCount = retryAttempts.get(requestKey) || 0;
      
      // Only retry once to prevent infinite loops
      // Only retry if it's actually a CSRF error (not just any 403)
      if (retryCount < 1 && !originalRequest.url?.includes('/auth/login/')) {
        const errorMessage = error.response?.data?.detail || error.response?.data?.error || '';
        const errorText = errorMessage.toLowerCase();
        
        // Only retry if it's definitely a CSRF error
        // Django CSRF errors: "CSRF", "incorrect", "missing", "origin checking failed"
        const isDefinitelyCsrfError = errorText.includes('csrf') || 
                                      errorText.includes('incorrect') ||
                                      errorText.includes('origin checking failed') ||
                                      errorText.includes('csrf token missing') ||
                                      errorText.includes('csrf verification failed');
        
        if (isDefinitelyCsrfError) {
          console.warn('CSRF error detected, fetching new token and retrying once...');
          try {
            // Mark this request as retried BEFORE making the retry
            retryAttempts.set(requestKey, retryCount + 1);
            // Clear cached token so we get a fresh one (Django may have rotated it)
            csrfTokenFromApi = null;
            
            // Fetch new CSRF token
            const csrfToken = await fetchCsrfToken();
            if (csrfToken) {
              originalRequest.headers['X-CSRFToken'] = csrfToken;
              return apiClient(originalRequest);
            } else {
              // Remove the retry mark if we couldn't get a token
              retryAttempts.delete(requestKey);
            }
          } catch (csrfError) {
            console.error('Failed to refresh CSRF token:', csrfError);
            // Remove the retry mark on error
            retryAttempts.delete(requestKey);
          }
        } else {
          // Not a CSRF error - don't retry, just reject
          console.warn('403 error but not a CSRF error, not retrying. Error:', errorMessage);
        }
      } else {
        // Already retried or not eligible for retry
        if (retryCount >= 1) {
          console.warn('Request already retried once, not retrying again');
          // Clean up the retry tracking after a delay
          setTimeout(() => retryAttempts.delete(requestKey), 5000);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

